import { useMemo } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { faEnvelope, faFamily, faFlask, faUserDoctor, faUsers } from '@fortawesome/pro-solid-svg-icons';

import { BridgeBioMaterialCode } from '@components/marketing/BridgeBioMaterialCode/BridgeBioMaterialCode';
import { CasrcadeLockup } from '@components/marketing/CasrcadeLockup/CasrcadeLockup';
import { PageIntro } from '@components/PageIntro/PageIntro';
import { ProcessBand, ProcessBandStep } from '@components/ProcessBand/ProcessBand';
import type { FVTReferralDetails } from '@customTypes/referrals';
import { pluralize } from '@helpers/string';
import { useReferralContext } from '@pages/referrals/ReferralRoot';

import { FamilyFaqSection } from './components/FamilyFaqSection';
import { FamilySidebar } from './components/FamilySidebar';
import { FamilySupportStrip } from './components/FamilySupportStrip';
import { InvitedRelativesCard } from './components/InvitedRelativesCard';

export const getFamilyProcessSteps = (referral: FVTReferralDetails): ProcessBandStep[] => {
  const invitedCount = referral.family_members.length;
  const isProband = referral.is_proband;

  const relativeTerm = pluralize({
    count: invitedCount,
    singular: 'relative',
    plural: 'relatives',
  });

  return [
    {
      label: '01',
      title: isProband ? 'Provider referred you' : 'Family member referred you',
      description: isProband ? 'Your care team sent you here.' : 'Your family member sent you here.',
      state: 'done',
      icon: isProband ? faUserDoctor : faFamily,
    },
    {
      label: '02',
      title: invitedCount > 0 ? `You've invited ${invitedCount} ${relativeTerm}` : 'You invite relatives',
      description:
        invitedCount > 0 ? 'You can invite more any time.' : 'Add eligible blood relatives (up to 4th degree).',
      state: 'current',
      icon: faUsers,
    },
    {
      label: '03',
      title: 'We email each one',
      description: 'Private invitation; they opt in on their own.',
      state: 'upcoming',
      icon: faEnvelope,
    },
    {
      label: '04',
      title: 'No-charge testing for them',
      description: 'Plus a genetic counselor consult.',
      state: 'upcoming',
      icon: faFlask,
    },
  ];
};

export const FvtFamily = () => {
  const { referral } = useReferralContext();

  const processSteps = useMemo(() => getFamilyProcessSteps(referral), [referral]);

  return (
    <div className='pt-4 w-100 bg-white'>
      <Container fluid='xxl' as='main' className='pb-5'>
        <CasrcadeLockup />

        <PageIntro className='mb-4' title='Family Cascade Genetic Testing' />

        <Row className='g-4 g-lg-5 align-items-start'>
          <Col lg={8}>
            <InvitedRelativesCard referral={referral} />

            <ProcessBand
              heading='How it works'
              id='fvt-family-process'
              introLead='Your relatives each get a private invitation and no-charge testing if they opt in.'
              steps={processSteps}
              variant='fvt'
            />

            <FamilyFaqSection />
            <FamilySupportStrip />
          </Col>

          <Col lg={4}>
            <FamilySidebar />
          </Col>
        </Row>

        <div className='pt-4'>
          <BridgeBioMaterialCode />
        </div>
      </Container>
    </div>
  );
};
